/**
 * Signal-flip alert subscriptions — one shared hook for the trading-pane
 * bell and the Preferences section, so both surfaces read/write the same
 * react-query cache entry.
 *
 * `locked` = the caller's tier can't use alerts (free, or the API said
 * 402 tier_required). Locked mode never fetches and `toggle` no-ops.
 * Toggle is optimistic: cache updates immediately, reverts on error.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import type { AlertSubscriptionsResponse } from "@/api/types";
import { useSubscription } from "@/billing/api";

export const ALERT_SUBSCRIPTIONS_KEY = ["alerts", "subscriptions"] as const;

export function useAlertSubscriptions() {
  const qc = useQueryClient();
  const { data: billing } = useSubscription();
  const tierLocked = billing?.tier === "free";

  const query = useQuery({
    queryKey: ALERT_SUBSCRIPTIONS_KEY,
    queryFn: api.getAlertSubscriptions,
    enabled: !tierLocked,
    retry: false,
    staleTime: 60_000,
  });

  const apiLocked = query.error instanceof ApiError && query.error.status === 402;
  const locked = tierLocked || apiLocked;
  const subscribed = useMemo(() => new Set(query.data?.material_ids ?? []), [query.data?.material_ids]);

  const mutation = useMutation({
    mutationFn: ({ materialId, isSubscribed }: { materialId: string; isSubscribed: boolean }) =>
      isSubscribed ? api.unsubscribeAlert(materialId) : api.subscribeAlert(materialId),
    onMutate: async ({ materialId, isSubscribed }) => {
      await qc.cancelQueries({ queryKey: ALERT_SUBSCRIPTIONS_KEY });
      const prev = qc.getQueryData<AlertSubscriptionsResponse>(ALERT_SUBSCRIPTIONS_KEY);
      qc.setQueryData<AlertSubscriptionsResponse>(ALERT_SUBSCRIPTIONS_KEY, (cur) => {
        const ids = new Set(cur?.material_ids ?? []);
        if (isSubscribed) ids.delete(materialId);
        else ids.add(materialId);
        return { material_ids: [...ids].sort() };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(ALERT_SUBSCRIPTIONS_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ALERT_SUBSCRIPTIONS_KEY }),
  });

  const { mutateAsync } = mutation;
  const toggle = useCallback(
    (materialId: string) => {
      if (locked) return Promise.resolve();
      return mutateAsync({
        materialId,
        isSubscribed: subscribed.has(materialId),
      });
    },
    [locked, subscribed, mutateAsync],
  );

  return {
    subscribed,
    locked,
    loading: !tierLocked && query.isLoading,
    toggle,
  };
}
